import React, { useState } from 'react';
import './StackableChart.css';

const StackableChart = ({ data, isLoading, error, title }) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipVisible, setTooltipVisible] = useState(false);
  if (isLoading) {
    return (
      <div className="stackable-chart-content">
        <div className="loading-spinner">Loading chart data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stackable-chart-content">
        <div className="error-message">Error loading chart: {error}</div>
      </div>
    );
  }

  if (!data || !data.devicePerformance || data.devicePerformance.length === 0) {
    return (
      <div className="stackable-chart-content">
        <div className="no-data-message">No data available for the selected time period.</div>
      </div>
    );
  }

  // Helper function to get FDBK score class
  const getFdbkClass = (score, hasData) => {
    if (!hasData || score === null) return 'no-data';
    if (score >= 70) return 'good-fdbk';
    if (score >= 40) return 'warning-fdbk';
    return 'poor-fdbk';
  };

  // Helper function to format FDBK score display
  const formatFdbkScore = (score, hasData) => {
    if (!hasData || score === null) return 'N/A';
    return score.toFixed(1);
  };

  // Handle mouse enter for tooltip
  const handleMouseEnter = (event, deviceData, index) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({ 
      x: event.clientX, 
      y: rect.top 
    });
    setHoveredItem({ ...deviceData, index });
    setTooltipVisible(true);
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setTooltipVisible(false);
    setHoveredItem(null);
  };

  // No need for mouse move tracking with modal

  return (
    <div className="stackable-chart-content">
      
      {/* Summary stats */}
      <div className="chart-summary">
        <div className="summary-item">
          <span className="summary-label">Total Responses:</span>
          <span className="summary-value">{data.totalResponses?.toLocaleString() || 0}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Average Score:</span>
          <span className="summary-value">{data.averageFdbkScore?.toFixed(1) || '0.0'}</span>
        </div>
        {data.bestKiosk && (
          <div className="summary-item">
            <span className="summary-label">Top Performer:</span>
            <span className="summary-value best-performer">
              {data.bestKiosk.storeName} ({data.bestKiosk.fdbkScore?.toFixed(1)})
            </span>
          </div>
        )}
      </div>

      {/* Column Headers */}
      <div className="chart-headers">
        <div className="header-label">Kiosk</div>
        <div className="header-bar">Response Distribution</div>
        <div className="header-metrics">
          <div className="header-total">Total</div>
          <div className="header-fdbk">Score</div>
        </div>
      </div>

      {/* Chart Data */}
      <div className="stackable-chart-data">
        {data.devicePerformance.map((device, index) => {
          const total = device.total || 0;
          const poorPercent = total > 0 ? (device.poor / total) * 100 : 0;
          const warningPercent = total > 0 ? (device.warning / total) * 100 : 0;
          const goodPercent = total > 0 ? (device.good / total) * 100 : 0;
          
          const fdbkClass = getFdbkClass(device.fdbkScore, device.hasData);
          const fdbkDisplay = formatFdbkScore(device.fdbkScore, device.hasData);

          return (
            <div 
              key={index} 
              className="stackable-item"
              onMouseEnter={(e) => handleMouseEnter(e, device, index)}
              onMouseLeave={handleMouseLeave}
            >
              <div className="stackable-label" title={device.label}>
                {device.label}
              </div>
              <div className="stackable-bar-container">
                {total > 0 ? (
                  <>
                    <div 
                      className="stackable-segment poor" 
                      style={{ width: `${poorPercent}%` }}
                      title={`Poor: ${device.poor} responses (${poorPercent.toFixed(1)}%)`}
                    >
                      {device.poor > 0 && device.poor}
                    </div>
                    <div 
                      className="stackable-segment warning" 
                      style={{ width: `${warningPercent}%` }}
                      title={`Average: ${device.warning} responses (${warningPercent.toFixed(1)}%)`}
                    >
                      {device.warning > 0 && device.warning}
                    </div>
                    <div 
                      className="stackable-segment good" 
                      style={{ width: `${goodPercent}%` }}
                      title={`Good: ${device.good} responses (${goodPercent.toFixed(1)}%)`}
                    >
                      {device.good > 0 && device.good}
                    </div>
                  </>
                ) : (
                  <div className="stackable-segment no-data" style={{ width: '100%' }}>
                    No Data
                  </div>
                )}
              </div>
              <div className="stackable-metrics">
                <div className="stackable-total">
                  {total > 0 ? total : 'N/A'}
                </div>
                <div className={`stackable-fdbk ${fdbkClass}`}>
                  {fdbkDisplay}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Simple Tooltip */}
      {tooltipVisible && hoveredItem && (
        <div 
          className="simple-tooltip" 
          style={{
            position: 'fixed',
            left: tooltipPosition.x + 10,
            top: tooltipPosition.y - 80,
            zIndex: 1000,
            pointerEvents: 'none'
          }}
        >
          <div className="tooltip-title">{hoveredItem.label}</div>
          <div className="tooltip-stats">
            <div>Poor: {hoveredItem.poor || 0} ({hoveredItem.total > 0 ? ((hoveredItem.poor || 0) / hoveredItem.total * 100).toFixed(1) : 0}%)</div>
            <div>Average: {hoveredItem.warning || 0} ({hoveredItem.total > 0 ? ((hoveredItem.warning || 0) / hoveredItem.total * 100).toFixed(1) : 0}%)</div>
            <div>Good: {hoveredItem.good || 0} ({hoveredItem.total > 0 ? ((hoveredItem.good || 0) / hoveredItem.total * 100).toFixed(1) : 0}%)</div>
            <div><strong>Total: {hoveredItem.total || 0}</strong></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StackableChart;